/**
 * components/rightbar/tabRegistry.ts —— tab **类型**注册与认领（纯函数，零副作用）。
 *
 * 真源：`102:frontend_src/src/rightbar/tabRegistry.js`（239 行）——抄结构/惯例，译 JS→TS。
 * 依据：设计件 §6.2（**tab 类型两阶段注册** ＋ 照 VS Code 的认领规则：glob → `canOpen` → 档位 →
 *       命中模式长度 → 注册序）／§6.4（内置 tab 类型）。
 *
 * 🔴 纪律（逐条承源件）：
 *   · 禁 `if (kind === …)` 分派 —— 一切经本表查（与正文注册表 `TabBodySeat::registerBody` 同款）；
 *   · `FALLBACK_TAB_KIND` 为**单一常量源**（源件批4 定：`dockPlanner`／`useSurface`／`AppShell`／
 *     `RightTabStrip` 一律引用本常量，**禁各写一份字面量**）；
 *   · **默认页＝声明**：`default: true` 标在产物视图（`files`）⇒ 首次展开播种取**声明类型**，
 *     不在调用处硬编码类型名（承 102 批4 大王 v1.7 订正逐字「原产物视图页面不改，默认页仍为产物视图页」）；
 *   · `text` 是**地址驱动**类型（`canOpen` 只认 `sononux://file/…`）：**无地址就没有页** ⇒ 无独立入口
 *     （承 20260920 大王「文件预览按钮首次打开时都没有文件根本无可以预览的内容，交互逻辑是错的」）。
 */

/** 认领优先级（顺序固定，不可改；源件逐字） */
export const CLAIM_ORDER = Object.freeze(["glob", "canOpen", "tier", "modeLen", "registryOrder"]);

/** 优先级档位数值（extension 最高） */
const TIER_RANK: Record<string, number> = Object.freeze({ extension: 3, builtin: 2, fallback: 1 });

/**
 * **兜底页 kind**：地址无人认领／指定 kind 未注册／`canOpen` 否决 时的落点，
 * 亦为停靠面「空格补位」「唯一不可关」判据所用的同一 kind。
 */
export const FALLBACK_TAB_KIND = "progress";

export interface TabTypeDef {
  id: string;
  kind: string;
  patterns: string[];
  priority?: string;
  canOpen?: ((address: string) => boolean) | null;
  /** 标题（常量或按地址生成） */
  title: string | ((address: string) => string);
  /** 默认页声明（`true` ＝「首次展开播种」落点；全表**至多一个**） */
  default?: boolean;
  /**
   * **按类型保活**（★20261011 PFK4·L-PF-B12／规划件 §7.3 #16）：`true` ⇒ 已访问正文
   * **隐藏而非卸载**（跨 tab／切会话／折叠不重挂），并同批两条配套——保活面**可刷新**
   * （切换回来触发刷新命令）＋**关闭释放**（`registerCloseHandler` 句柄释放）。缺省 `false`＝旧行为。
   * **不加** `multiple`（§7.3 #18：普通页按「同一 (kind,地址)」唯一，去重复用 `openKind` 逻辑）。
   */
  keepMounted?: boolean;
}

const REGISTRY: TabTypeDef[] = [];

/** 注册一个 tab 类型（同 id 二次注册 ⇒ 抛错，承源件纪律） */
export function register(def: TabTypeDef): void {
  if (REGISTRY.some((r) => r.id === def.id)) throw new Error(`tabRegistry: duplicate id "${def.id}"`);
  REGISTRY.push({ ...def, patterns: def.patterns || [], canOpen: def.canOpen || null, default: def.default === true, keepMounted: def.keepMounted === true });
}

/** 按类型查「保活」声明（缺省 false＝旧行为；**禁**在调用处硬编码类型名，同 `defaultKind` 纪律） */
export function isKeepMounted(kind: string): boolean {
  const entry = entryOfKind(kind);
  return entry ? entry.keepMounted === true : false;
}

/** 注册表快照（只读） */
export function getRegistry(): TabTypeDef[] {
  return REGISTRY.slice();
}

/** glob 匹配（支持 `**` 与 `*`；承源件实现逐字） */
function matchGlob(pattern: string, address: string): boolean {
  if (!pattern) return false;
  const regexStr = pattern
    .replace(/[.+^${}()|[\]\\]/g, "\\$&")
    .replace(/\*\*/g, "{{DOUBLE_STAR}}")
    .replace(/\*/g, "[^/]*")
    .replace(/\{\{DOUBLE_STAR\}\}/g, ".*");
  return new RegExp(`^${regexStr}$`, "i").test(address);
}

/** 候补：该地址可被哪些页类型接管（按认领优先级排序） */
export function candidates(address: string): Array<{ id: string; kind: string; tier: string; modeLen: number; registryOrder: number }> {
  const results: Array<{ id: string; kind: string; tier: string; modeLen: number; registryOrder: number }> = [];
  for (let i = 0; i < REGISTRY.length; i += 1) {
    const entry = REGISTRY[i];
    const globHit = entry.patterns.some((p) => matchGlob(p, address));
    let canOpenResult = true;
    if (entry.canOpen && (globHit || entry.patterns.length === 0)) canOpenResult = entry.canOpen(address);
    if (entry.patterns.length === 0 && !entry.canOpen) continue;
    if (globHit && canOpenResult) {
      results.push({
        id: entry.id,
        kind: entry.kind,
        tier: entry.priority || "extension",
        modeLen: Math.max(...entry.patterns.filter((p) => matchGlob(p, address)).map((p) => p.length)),
        registryOrder: i,
      });
    }
  }
  results.sort((a, b) => {
    const tierDiff = (TIER_RANK[b.tier] || 0) - (TIER_RANK[a.tier] || 0);
    if (tierDiff !== 0) return tierDiff;
    const modeDiff = b.modeLen - a.modeLen;
    if (modeDiff !== 0) return modeDiff;
    return a.registryOrder - b.registryOrder;
  });
  return results;
}

function entryOfKind(kind: string): TabTypeDef | null {
  return REGISTRY.find((r) => r.kind === kind) || null;
}

function fallbackEntry(): TabTypeDef | null {
  return entryOfKind(FALLBACK_TAB_KIND);
}

/**
 * 认领：按 kind 或地址定案一个 tab 类型。
 * · 指定 kind ⇒ 跳过 glob、**保留 canOpen**（无地址 ⇒ 地址驱动类型被否决 ⇒ 兜底）；
 * · 未指定 ⇒ 走 `candidates` 排序取首个；无命中 ⇒ 兜底。
 */
export function claim(address = "", kind?: string): { entry: TabTypeDef | null; kind: string; matched: boolean } {
  if (kind) {
    const entry = entryOfKind(kind);
    if (!entry) return { entry: fallbackEntry(), kind: FALLBACK_TAB_KIND, matched: false };
    if (entry.canOpen && !entry.canOpen(address)) return { entry: fallbackEntry(), kind: FALLBACK_TAB_KIND, matched: false };
    return { entry, kind: entry.kind, matched: true };
  }
  const cands = candidates(address);
  if (cands.length === 0) return { entry: fallbackEntry(), kind: FALLBACK_TAB_KIND, matched: false };
  const best = cands[0];
  return { entry: REGISTRY.find((r) => r.id === best.id) || null, kind: best.kind, matched: true };
}

/** tab 标题（常量或按地址生成） */
export function titleOf(kind: string, address = ""): string {
  const entry = entryOfKind(kind);
  if (!entry) return kind;
  return typeof entry.title === "function" ? entry.title(address) : entry.title;
}

/** **默认页 kind**（取声明；无声明 ⇒ 兜底 kind）——调用处禁硬编码类型名 */
export function defaultKind(): string {
  const hit = REGISTRY.find((r) => r.default === true);
  return hit ? hit.kind : FALLBACK_TAB_KIND;
}

// ── 内置类型注册（与正文注册表 `TabBodySeat::BODIES` 的 kind 集合**必须一致**，判据 `rightbar_tab_kinds_registered`）──
// ★20261011 PFK4·L-PF-B12：`files`／`text` 两类型声明 `keepMounted`（有状态面——产物视图滚动/折叠态、
//   文件预览滚动/行号态值得保活；`progress` 兜底页不保活＝折叠零代价不破）。回滚＝改回 false（等价旧行为）。

register({ id: "builtin:progress", kind: FALLBACK_TAB_KIND, patterns: [], priority: "builtin", title: "任务进展" });

register({ id: "builtin:files", kind: "files", patterns: [], priority: "builtin", title: "产物视图", default: true, keepMounted: true });

register({
  id: "builtin:text",
  kind: "text",
  patterns: ["sononux://file/**"],
  priority: "fallback",
  canOpen: (address) => address.startsWith("sononux://file/"),
  keepMounted: true,
  title: (address) => {
    const match = address.match(/sononux:\/\/file\/.*\/id\/([^/?]+)/);
    return match ? `文件预览 ${match[1].slice(0, 8)}` : "文件预览";
  },
});
